import { useState } from 'react';
import { toast } from 'react-toastify';
import { Users, Shield, Crown, Eye, Briefcase, UserPlus, Mail, Trash2, X } from 'lucide-react';
import { Button } from '@components/ui';

// Roller
const ROLES = [
    {
        id: 'admin',
        label: 'Sistem Yöneticisi',
        description: 'Tüm yetkilere sahip.',
        color: 'error',
        icon: Shield,
        permissions: ['all']
    },
    {
        id: 'owner',
        label: 'Şirket Sahibi',
        description: 'Finansal verileri, raporları ve ekip üyelerini yönetebilir.',
        color: 'warning',
        icon: Crown,
        permissions: ['users', 'finance', 'reports', 'constructions', 'settings']
    },
    {
        id: 'manager',
        label: 'Şantiye Şefi',
        description: 'İnşaat, blok, daire ekleyebilir ve ödeme kaydı girebilir.',
        color: 'primary',
        icon: Briefcase,
        permissions: ['constructions', 'blocks', 'apartments', 'payments', 'reports']
    },
    {
        id: 'viewer',
        label: 'İzleyici',
        description: 'Tüm verileri görüntüler ancak değişiklik yapamaz.',
        color: 'tertiary',
        icon: Eye,
        permissions: ['view']
    },
];

// Mock team members
const mockTeamMembers = [
    { id: 1, name: 'Mücahit Çetin', email: 'mucahit@insaet.com', role: 'admin', avatar: 'MÇ', status: 'active' },
    { id: 2, name: 'Ömer Çetin', email: 'omer@insaet.com', role: 'owner', avatar: 'ÖÇ', status: 'active' },
    { id: 3, name: 'Abdurrahim Çetin', email: 'abdurrahim@insaet.com', role: 'owner', avatar: 'ARÇ', status: 'active' },
    { id: 4, name: 'Mehmet Kaya', email: 'mehmet@insaet.com', role: 'manager', avatar: 'MK', status: 'active' },
    { id: 5, name: 'Ali Demir', email: 'ali@insaet.com', role: 'viewer', avatar: 'AD', status: 'pending' },
];

export default function TeamSettings() {
    const [teamMembers, setTeamMembers] = useState(mockTeamMembers);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('viewer');

    const colorClasses = {
        error: 'bg-error/10 text-error',
        warning: 'bg-warning/10 text-warning',
        primary: 'bg-primary/10 text-primary',
        tertiary: 'bg-tertiary/10 text-tertiary',
    };

    const handleInvite = () => {
        if (!inviteEmail) {
            toast.error('E-posta adresi girin');
            return;
        }
        toast.success(`${inviteEmail} adresine davet gönderildi`);
        setInviteEmail('');
        setShowInviteModal(false);
    };

    const handleRemoveMember = (member) => {
        if (member.role === 'admin') {
            toast.error('Sistem yöneticisi kaldırılamaz');
            return;
        }
        setTeamMembers(prev => prev.filter(m => m.id !== member.id));
        toast.success(`${member.name} ekipten çıkarıldı`);
    };

    const handleRoleChange = (memberId, newRole) => {
        setTeamMembers(prev => prev.map(m =>
            m.id === memberId ? { ...m, role: newRole } : m
        ));
        toast.success('Kullanıcı rolü güncellendi');
    };

    return (
        <div className="space-y-6">
            {/* Header - Responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-text-primary">Ekip Yönetimi</h2>
                    <p className="text-text-tertiary text-sm mt-1">Ekip üyelerini ve rollerini yönetin</p>
                </div>
                <Button
                    variant="primary"
                    icon={<UserPlus size={18} />}
                    onClick={() => setShowInviteModal(true)}
                    className="w-full sm:w-auto"
                >
                    Davet Et
                </Button>
            </div>

            {/* Roles Info - Responsive Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {ROLES.map((role) => {
                    const Icon = role.icon;
                    return (
                        <div
                            key={role.id}
                            className="bg-bg-tertiary/30 rounded-xl border border-border-light p-3 sm:p-4"
                        >
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3 ${colorClasses[role.color]}`}>
                                <Icon size={16} className="sm:hidden" />
                                <Icon size={20} className="hidden sm:block" />
                            </div>
                            <h4 className="font-semibold text-text-primary text-xs sm:text-sm">{role.label}</h4>
                            <p className="text-xs text-text-tertiary mt-1 hidden sm:block">{role.description}</p>
                        </div>
                    );
                })}
            </div>

            {/* Team Members List - Responsive */}
            <div className="bg-bg-tertiary/30 rounded-xl border border-border-light overflow-hidden">
                <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-border-light flex items-center gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Users size={18} className="sm:hidden text-primary" />
                        <Users size={20} className="hidden sm:block text-primary" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-text-primary text-sm sm:text-base">Ekip Üyeleri</h3>
                        <p className="text-xs sm:text-sm text-text-tertiary">{teamMembers.length} kullanıcı</p>
                    </div>
                </div>

                <div className="divide-y divide-border-light">
                    {teamMembers.map((member) => {
                        const role = ROLES.find(r => r.id === member.role);
                        const RoleIcon = role?.icon || Eye;
                        return (
                            <div key={member.id} className="px-4 sm:px-5 py-3 sm:py-4">
                                {/* Mobile Layout */}
                                <div className="flex flex-col gap-3 sm:hidden">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-medium text-xs shrink-0">
                                            {member.avatar}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-medium text-text-primary text-sm truncate">{member.name}</p>
                                                {member.status === 'pending' && (
                                                    <span className="px-2 py-0.5 text-xs rounded-full bg-warning/10 text-warning shrink-0">Bekliyor</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-text-tertiary truncate">{member.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${colorClasses[role?.color || 'tertiary']}`}>
                                            <RoleIcon size={12} />
                                            <span className="truncate">{role?.label}</span>
                                        </div>
                                        {member.role !== 'admin' && (
                                            <div className="flex items-center gap-2">
                                                <select
                                                    className="px-2 py-1.5 rounded-lg border border-border-light bg-bg-primary text-xs text-text-primary focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all cursor-pointer"
                                                    value={member.role}
                                                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                                                >
                                                    {ROLES.filter(r => r.id !== 'admin').map(r => (
                                                        <option key={r.id} value={r.id}>{r.label}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    onClick={() => handleRemoveMember(member)}
                                                    className="p-1.5 rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors cursor-pointer"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Desktop Layout */}
                                <div className="hidden sm:flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-4 min-w-0">
                                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-medium text-sm shrink-0">
                                            {member.avatar}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-text-primary truncate">{member.name}</p>
                                                {member.status === 'pending' && (
                                                    <span className="px-2 py-0.5 text-xs rounded-full bg-warning/10 text-warning">Bekliyor</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-text-tertiary truncate">{member.email}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 shrink-0">
                                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${colorClasses[role?.color || 'tertiary']}`}>
                                            <RoleIcon size={14} />
                                            {role?.label}
                                        </div>

                                        {member.role !== 'admin' && (
                                            <div className="flex items-center gap-1">
                                                <select
                                                    className="px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-sm text-text-primary focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all cursor-pointer"
                                                    value={member.role}
                                                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                                                >
                                                    {ROLES.filter(r => r.id !== 'admin').map(r => (
                                                        <option key={r.id} value={r.id}>{r.label}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    onClick={() => handleRemoveMember(member)}
                                                    className="p-2 rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors cursor-pointer"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Invite Modal - Responsive */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                    <div className="bg-bg-secondary rounded-t-2xl sm:rounded-2xl border border-border-light p-5 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-text-primary">Ekip Üyesi Davet Et</h3>
                            <button
                                onClick={() => setShowInviteModal(false)}
                                className="p-2 rounded-lg text-text-muted hover:bg-bg-tertiary transition-colors sm:hidden"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-2">E-posta Adresi</label>
                                <div className="relative">
                                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                                    <input
                                        type="email"
                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-border-light bg-bg-primary text-text-primary placeholder:text-text-muted focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                        placeholder="ornek@email.com"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-2">Rol</label>
                                <select
                                    className="w-full px-4 py-3 rounded-xl border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all cursor-pointer"
                                    value={inviteRole}
                                    onChange={(e) => setInviteRole(e.target.value)}
                                >
                                    {ROLES.filter(r => r.id !== 'admin').map(r => (
                                        <option key={r.id} value={r.id}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
                            <Button variant="ghost" className="flex-1" onClick={() => setShowInviteModal(false)}>
                                İptal
                            </Button>
                            <Button variant="primary" className="flex-1" icon={<UserPlus size={18} />} onClick={handleInvite}>
                                Davet Gönder
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
